/**
 * Nmbrs Override Hooks
 *
 * React Query hooks for managing per-field overrides on employee data.
 * Story Nmbrs-2.3: Override Beheer per Medewerker
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../../lib/api';

// ============================================
// Types
// ============================================

export interface NmbrsFieldOverride {
  id: number;
  employeeId: number;
  fieldName: string;
  overrideValue: string;
  nmbrsValue: string | null;
  overriddenBy: number;
  overriddenAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface FieldComparison {
  fieldName: string;
  fieldLabel: string;
  localValue: string | null;
  nmbrsValue: string | null;
  hasOverride: boolean;
  overrideValue: string | null;
  overriddenBy: number | null;
  overriddenAt: string | null;
}

interface OverridesResponse {
  data: NmbrsFieldOverride[];
}

interface OverrideResponse {
  data: NmbrsFieldOverride;
}

interface ComparisonResponse {
  data: FieldComparison[];
}

// ============================================
// Hooks
// ============================================

/**
 * Fetch all active overrides for an employee.
 * AC1: Returns all active overrides.
 */
export function useEmployeeOverrides(employeeId: number | undefined) {
  return useQuery({
    queryKey: ['nmbrs', 'overrides', employeeId],
    queryFn: () => apiRequest<OverridesResponse>(`/nmbrs/overrides/${employeeId}`),
    enabled: !!employeeId,
  });
}

/**
 * Fetch field comparison (local vs Nmbrs) for a linked employee.
 * AC5: Shows field comparison table.
 */
export function useFieldComparison(employeeId: number | undefined) {
  return useQuery({
    queryKey: ['nmbrs', 'overrides', employeeId, 'comparison'],
    queryFn: () => apiRequest<ComparisonResponse>(`/nmbrs/overrides/${employeeId}/comparison`),
    enabled: !!employeeId,
    staleTime: 2 * 60 * 1000, // 2 min cache to limit SOAP calls
  });
}

/**
 * Set (create/update) an override for an employee field.
 * AC2, AC6: Creates override, toggle switch enables.
 */
export function useSetOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { employeeId: number; fieldName: string; overrideValue: string }) =>
      apiRequest<OverrideResponse>(`/nmbrs/overrides/${data.employeeId}`, {
        method: 'POST',
        body: JSON.stringify({
          fieldName: data.fieldName,
          overrideValue: data.overrideValue,
        }),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'overrides', variables.employeeId] });
    },
  });
}

/**
 * Remove an override for an employee field.
 * AC3, AC6: Removes override, toggle switch disables.
 */
export function useRemoveOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { employeeId: number; fieldName: string }) =>
      apiRequest<{ data: { success: boolean } }>(
        `/nmbrs/overrides/${data.employeeId}/${data.fieldName}`,
        { method: 'DELETE' }
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'overrides', variables.employeeId] });
    },
  });
}
